import {
    CustomTableFilterBar,
    ETypes,
    RColumn,
    RemoteTable,
    RemoteTableRef,
    stringFilterTemplate,
} from 'medere-ui-framework';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView, TabViewTabChangeEvent } from 'primereact/tabview';
import { Tag } from 'primereact/tag';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import ExportMenu, { ExportFormat } from '../../components/novarium/ExportMenu';
import { formatNumberRange, numberRangeFilterTemplate } from '../../components/novarium/NumberRangeFilter';
import NvTooltips from '../../components/novarium/NvTooltips';
import { EMPTY_VALUE, formatIsoMoment, textOrDash } from '../../components/summary/summaryFormat';
import { clearPersistedTableState, downloadBlob } from '../../components/summary/summaryTable';
import '../../components/summary/Summary.scss';
import {
    NewBornGranularity,
    NewBornOptionsDTO,
    NewBornReviewReason,
    NewBornRiskFlag,
    NewBornRowDTO,
} from '../../dto/newborn/NewBornDTO';
import useNotify from '../../hooks/useNotify';
import { parseApiError } from '../../services/apiError';
import {
    NewBornTableParams,
    exportNewBornReport,
    getNewBornItems,
    getNewBornOptions,
    getNewBornReportPdf,
    isAccessDenied,
    isRequestCanceled,
} from '../../services/NewBornController';
import { formatCount } from '../UrgencySummary/urgencySummaryFormat';
import NewBornEvolution from './NewBornEvolution';
import NewBornFilters from './NewBornFilters';
import NewBornIndicators from './NewBornIndicators';
import { NewBornMeasure } from './newBornEvolutionModel';
import useColumnHeaderTips from './useColumnHeaderTips';
import useNewBornFilters from './useNewBornFilters';
import useNewBornSummary from './useNewBornSummary';
import './NewBornView.scss';

/** Clave con la que RemoteTable guarda filtros y orden en el navegador. */
const TABLE_ID = 'NewBornTable';
const DEFAULT_SORT = [{ field: 'birthdatetime', order: -1 as const }];

/** Las pestañas bajo los indicadores, por posición en el `TabView`. */
const TAB = { list: 0, evolution: 1 } as const;

/** Mínimo de casos para «mejor» o «peor» mientras no llegó el resumen: el que manda hoy el backend (`minCasesForVerdict`). */
const FALLBACK_MIN_CASES = 30;

/**
 * Tipo de dato de cada columna con filtro de la barra: define cómo viaja el filtro al backend. Texto: «contiene».
 * Números: rango desde / hasta. El período y los combos de «Más filtros» no son de columna: los manda la pantalla.
 */
const FILTERS_TYPE = [
    { field: 'patientname', type: ETypes.string },
    { field: 'babyName', type: ETypes.string },
    { field: 'clinicHistoryId', type: ETypes.string },
    { field: 'age', type: ETypes.integer },
    { field: 'weight', type: ETypes.bigDecimal },
    { field: 'apgar', type: ETypes.integer },
    { field: 'apgar5m', type: ETypes.integer },
    { field: 'babyage', type: ETypes.integer },
    { field: 'doctorname', type: ETypes.string },
];

/**
 * Ancho mínimo de cada columna, en px. Son los pesos (`grow`) de las columnas y su suma es el ancho mínimo de la tabla
 * (`tableMinWidth`): si el contenedor es más angosto, la tabla se desplaza hacia el costado en vez de achicar las
 * columnas hasta truncar las cabeceras.
 */
const COLUMN_WIDTH = {
    birth: 144,
    mother: 176,
    motherAge: 112,
    baby: 160,
    history: 96,
    sex: 88,
    delivery: 128,
    weight: 88,
    weeks: 104,
    apgar: 104,
    apgar5: 104,
    placement: 144,
    alerts: 184,
    review: 128,
    doctor: 168,
} as const;

/** Ancho de la columna de acciones por fila, que `RemoteTable` suma aparte. */
const ROW_ACTIONS_WIDTH = 64;

const TABLE_MIN_WIDTH = Object.values(COLUMN_WIDTH).reduce((sum, width) => sum + width, ROW_ACTIONS_WIDTH);

const RISK_LABEL_IDS: Record<NewBornRiskFlag, string> = {
    PREMATURE: 'NewBornFlagPremature',
    LOW_WEIGHT: 'NewBornFlagLowWeight',
    LOW_APGAR5: 'NewBornFlagLowApgar5',
};

const RISK_HINT_IDS: Record<NewBornRiskFlag, string> = {
    PREMATURE: 'NewBornFlagPrematureHint',
    LOW_WEIGHT: 'NewBornFlagLowWeightHint',
    LOW_APGAR5: 'NewBornFlagLowApgar5Hint',
};

/** Por qué una fila está «a revisar»: el texto va en el tooltip de su marca. */
const REVIEW_REASON_IDS: Record<NewBornReviewReason, string> = {
    MOTHER_IS_BABY: 'NewBornReviewMotherIsBaby',
    GESTATIONAL_AGE_IMPLAUSIBLE: 'NewBornReviewGestationalAge',
    APGAR_IMPLAUSIBLE: 'NewBornReviewApgar',
    WEIGHT_IMPLAUSIBLE: 'NewBornReviewWeight',
};

/** Resultado de la última consulta de la lista: `error` y `denied` no son una lista vacía. */
type ListState = 'ok' | 'error' | 'denied';

/**
 * Bebés recién nacidos: nacimientos registrados en el sitio, con los indicadores del período, la grilla (scroll infinito,
 * filtros y orden en el backend), la hoja de enfermería de la madre en PDF por fila y la exportación del listado a
 * Excel o PDF. Los filtros se aplican al instante: el período y «Más filtros» viajan en `filters` (los mismos en el
 * listado, el resumen y la exportación); los de columna, por la barra de la grilla.
 */
export default function NewBornView() {
    const intl = useIntl();
    const notify = useNotify();
    const tableRef = useRef<RemoteTableRef>(null);
    const tableBoxRef = useRef<HTMLDivElement>(null);

    const filtersData = useNewBornFilters();
    const { applied, pending } = filtersData;
    const { summary, status: summaryStatus, retry: retrySummary } = useNewBornSummary(applied);

    // Lo último que pidió la grilla (filtros de la pantalla, filtros de columna y buscador, y orden): es lo que ve el
    // usuario, así que es lo que exporta «Exportar»
    const lastLoadRef = useRef<NewBornTableParams | null>(null);

    // La pestaña, la medida y la agrupación de la evolución no se pierden al cambiar los filtros ni de pestaña
    const [tab, setTab] = useState<number>(TAB.list);
    const [measure, setMeasure] = useState<NewBornMeasure>('births');
    /** Agrupación elegida con «Semana | Mes»; `null` es la que decide el backend según el largo del período. */
    const [granularityChoice, setGranularityChoice] = useState<NewBornGranularity | null>(null);
    // Con otro período vuelve la agrupación que corresponde a su largo
    const { from: appliedFrom, to: appliedTo } = applied;
    useEffect(() => { setGranularityChoice(null); }, [appliedFrom, appliedTo]);
    const appliedRef = useRef(applied);
    appliedRef.current = applied;

    const [reportingId, setReportingId] = useState<number | null>(null);
    const [listState, setListState] = useState<ListState>('ok');
    // Opciones de los combos de «Más filtros»: se cargan una sola vez al abrir la pantalla
    const [options, setOptions] = useState<NewBornOptionsDTO | null>(null);

    // Antes del primer render de la tabla: abre sin filtros ni orden de una visita anterior
    useState(() => clearPersistedTableState(TABLE_ID));
    useEffect(() => () => clearPersistedTableState(TABLE_ID), []);

    useEffect(() => {
        const controller = new AbortController();
        getNewBornOptions(controller.signal)
            .then(setOptions)
            .catch((error: unknown) => {
                // La tabla sigue funcionando: los combos quedan sin opciones
                if (!controller.signal.aborted && !isRequestCanceled(error) && !isAccessDenied(error)) {
                    notify.error(intl.formatMessage({ id: 'NewBornOptionsError' }));
                }
            });
        return () => controller.abort();
        // Se pide una sola vez por apertura de la pantalla
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /** Los filtros de la pantalla llegan a `getItems` junto con los de columna y el orden de la tabla. */
    const tableParams = useMemo(() => ({ filters: applied }), [applied]);

    const loadItems = useCallback(async (offSet: number, pageSize: number, params?: NewBornTableParams) => {
        const request = params ?? {};
        lastLoadRef.current = request;
        try {
            const rows = await getNewBornItems(offSet, pageSize, request);
            setListState('ok');
            return rows;
        } catch (error: unknown) {
            if (!isRequestCanceled(error)) setListState(isAccessDenied(error) ? 'denied' : 'error');
            return [];
        }
    }, []);

    /**
     * Excel o PDF de lo que se ve: el período y «Más filtros», los filtros de columna de la barra «+ Filtro», el buscador y el
     * orden de la grilla (los últimos que pidió la tabla). El encabezado del archivo lo arma el backend con esos filtros.
     */
    const exportList = useCallback((format: ExportFormat) => {
        const last = lastLoadRef.current;
        if (!last) return Promise.reject(new Error('Listado sin consultar'));
        // Con la pestaña «Evolución» la grilla no está montada: sus filtros de columna y su orden son los últimos, y los de la
        // pantalla, los de ahora
        return exportNewBornReport(format, { ...last, filters: appliedRef.current });
    }, []);

    const handleReport = useCallback(async (row: NewBornRowDTO) => {
        const id = row.patientControlId;
        setReportingId(id);
        try {
            const blob = await getNewBornReportPdf({ patientControlId: id, healthPatientServiceId: row.healthPatientServiceId });
            if (blob == null) {
                notify.error(intl.formatMessage({ id: 'NewBornReportNoData' }));
                return;
            }
            downloadBlob(blob, `hoja_de_enfermeria_madre_${id}.pdf`);
            notify.success(intl.formatMessage({ id: 'NewBornReportDone' }));
        } catch (error: unknown) {
            const status = parseApiError(error).status;
            notify.error(intl.formatMessage({ id: status === 404 ? 'NewBornReportNotFound' : 'NewBornReportError' }));
        } finally {
            setReportingId(null);
        }
    }, [intl, notify]);

    const rowActions = useCallback((row: NewBornRowDTO) => {
        // El ícono dice a qué fila corresponde: la acción se repite en cada fila
        const label = intl.formatMessage({ id: 'NewBornReportActionRow' }, { mother: textOrDash(row.patientname) });
        return (
            // TODO(design-system): el ícono de la hoja de enfermería (imprimir) no está en la lista de íconos fijos por acción.
            <Button
                type="button"
                text
                className="nv-icon nv-tip"
                icon="pi pi-print"
                aria-label={label}
                data-pr-tooltip={intl.formatMessage({ id: 'NewBornReportAction' })}
                loading={reportingId === row.patientControlId}
                disabled={reportingId != null}
                onClick={() => handleReport(row)}
            />
        );
    }, [intl, reportingId, handleReport]);

    // Buscador por madre y filtros por campo. Los textos de la barra ya vienen en español en el framework
    // Edad de la madre, peso, APGAR y semanas de gestación: filtros de rango (desde / hasta)
    const rangeLabels = useMemo(() => ({
        from: intl.formatMessage({ id: 'FilterRangeFrom' }),
        to: intl.formatMessage({ id: 'FilterRangeTo' }),
    }), [intl]);
    const rangeFilter = useMemo(() => numberRangeFilterTemplate(rangeLabels), [rangeLabels]);
    const rangeText = useCallback((range: unknown) => formatNumberRange(range, rangeLabels), [rangeLabels]);

    const filterBar = useMemo<CustomTableFilterBar>(() => ({
        searchField: 'patientname',
        searchLabel: intl.formatMessage({ id: 'NewBornColMother' }),
        texts: { search: intl.formatMessage({ id: 'NewBornSearchMother' }) },
        // Las cabeceras son cortas: el filtro y su chip llevan el nombre completo
        fields: [
            { field: 'babyName', label: intl.formatMessage({ id: 'NewBornColBaby' }) },
            { field: 'clinicHistoryId', label: intl.formatMessage({ id: 'NewBornColHistoryFull' }) },
            { field: 'age', label: intl.formatMessage({ id: 'NewBornColMotherAgeFull' }), format: rangeText },
            { field: 'weight', label: intl.formatMessage({ id: 'NewBornColWeight' }), tag: intl.formatMessage({ id: 'NewBornUnitGrams' }), format: rangeText },
            { field: 'apgar', label: intl.formatMessage({ id: 'NewBornColApgarFull' }), format: rangeText },
            { field: 'apgar5m', label: intl.formatMessage({ id: 'NewBornColApgar5Full' }), format: rangeText },
            { field: 'babyage', label: intl.formatMessage({ id: 'NewBornColWeeksFull' }), tag: intl.formatMessage({ id: 'NewBornUnitWeeks' }), format: rangeText },
            { field: 'doctorname' },
        ],
    }), [intl, rangeText]);

    // Cabeceras cortas con su nombre completo como tooltip y descripción accesible
    const headerTips = useMemo(() => ({
        [intl.formatMessage({ id: 'NewBornColMotherAge' })]: intl.formatMessage({ id: 'NewBornColMotherAgeFull' }),
        [intl.formatMessage({ id: 'NewBornColHistory' })]: intl.formatMessage({ id: 'NewBornColHistoryFull' }),
        [intl.formatMessage({ id: 'NewBornColApgar' })]: intl.formatMessage({ id: 'NewBornColApgarFull' }),
        [intl.formatMessage({ id: 'NewBornColApgar5' })]: intl.formatMessage({ id: 'NewBornColApgar5Full' }),
        [intl.formatMessage({ id: 'NewBornColWeeks' })]: intl.formatMessage({ id: 'NewBornColWeeksFull' }),
    }), [intl]);
    useColumnHeaderTips(tableBoxRef, headerTips);

    const numberOrDash = (value: number | null) => (value == null ? EMPTY_VALUE : formatCount(intl, value));
    // Datos largos: truncados con «…» y el texto completo en el tooltip
    const textCell = (value: string | null) => {
        const text = textOrDash(value);
        return text === EMPTY_VALUE ? text : <span className="nv-truncate nv-tip" data-pr-tooltip={text}>{text}</span>;
    };

    const title = intl.formatMessage({ id: 'NewBornTitle' });

    if (summaryStatus === 'denied' || listState === 'denied') {
        return (
            <div className="nv-scope nv-page">
                <header className="nv-page-head">
                    <h1 className="nv-h1">{title}</h1>
                </header>
                <p className="nv-note" role="status">
                    <i className="pi pi-info-circle" aria-hidden="true" />
                    {intl.formatMessage({ id: 'NewBornAccessDenied' })}
                </p>
            </div>
        );
    }

    // Los indicadores que se ven no son los de los filtros: se están consultando o el cambio espera el retardo
    const busy = summaryStatus === 'loading' || pending;
    const showIndicators = summary !== null && summaryStatus !== 'error';
    const exportDisabledReason = busy ? intl.formatMessage({ id: 'NewBornExportLoading' })
        : listState === 'error' || summaryStatus === 'error' ? intl.formatMessage({ id: 'NewBornExportBlocked' })
            : summary !== null && summary.current.births === 0 ? intl.formatMessage({ id: 'NewBornExportEmpty' })
                : undefined;

    return (
        <div className="nv-scope nv-page nv-newborn-page">
            <NvTooltips />
            <div className="nv-page-head">
                <div className="nv-head-row">
                    <h1 className="nv-h1">{title}</h1>
                    <ExportMenu
                        fallbackFileName="recien-nacidos"
                        disabled={exportDisabledReason !== undefined}
                        disabledReason={exportDisabledReason}
                        onExport={exportList}
                    />
                </div>
            </div>

            <NewBornFilters data={filtersData} options={options} />

            <div className="nv-newborn-results" aria-busy={busy}>
                {busy && (
                    <div className="nv-newborn-loading" role="status">
                        <div className="nv-loading nv-newborn-loading__box">
                            <ProgressSpinner strokeWidth="4" style={{ width: 24, height: 24 }} />
                            <span>{intl.formatMessage({ id: 'NewBornLoading' })}</span>
                        </div>
                    </div>
                )}

                {summaryStatus === 'error' && !busy && (
                    <div className="nv-card nv-newborn-error" role="alert">
                        <p className="nv-note">
                            <i className="pi pi-info-circle" aria-hidden="true" />
                            {intl.formatMessage({ id: 'NewBornSummaryError' })}
                        </p>
                        <Button type="button" className="buttonMain2" outlined label={intl.formatMessage({ id: 'NewBornRetry' })} onClick={retrySummary} />
                    </div>
                )}

                {showIndicators && (
                    <div className={busy ? 'nv-newborn-blurred' : undefined}>
                        <NewBornIndicators summary={summary} applied={applied} onFilter={filtersData.patchFilters} />
                    </div>
                )}
            </div>

            {filtersData.rangeOrigin && (
                <div className="nv-newborn-range-back">
                    <Button
                        type="button"
                        link
                        icon="pi pi-angle-double-left"
                        label={intl.formatMessage({ id: 'NewBornReturnTo' }, { period: filtersData.rangeOrigin.label })}
                        onClick={filtersData.returnToPreviousRange}
                    />
                </div>
            )}

            <TabView activeIndex={tab} onTabChange={(event: TabViewTabChangeEvent) => setTab(event.index)}>
                <TabPanel header={intl.formatMessage({ id: 'NewBornTabList' })}>
                    {listState === 'error' && (
                        <div className="nv-card nv-newborn-error" role="alert">
                            <p className="nv-note">
                                <i className="pi pi-info-circle" aria-hidden="true" />
                                {intl.formatMessage({ id: 'NewBornListError' })}
                            </p>
                            <Button
                                type="button"
                                className="buttonMain2"
                                outlined
                                label={intl.formatMessage({ id: 'NewBornRetry' })}
                                onClick={() => tableRef.current?.refreshTable?.()}
                            />
                        </div>
                    )}

                    <div className="nv-newborn-table" ref={tableBoxRef}>
                        <RemoteTable
                            ref={tableRef}
                            idTable={TABLE_ID}
                            dataKey="patientControlId"
                            getItems={loadItems}
                            params={tableParams}
                            paramsDelay={0}
                            filterBar={filterBar}
                            filtersType={FILTERS_TYPE}
                            defaultMultiSortMeta={DEFAULT_SORT}
                            refreshButton
                            resizableColumns
                            tableMinWidth={TABLE_MIN_WIDTH}
                            rowActions={rowActions}
                            // Con error el cuerpo no dice «No hay recién nacidos»: lo que pasó lo dice el aviso de arriba
                            notFoundText={listState === 'error' ? ' ' : intl.formatMessage({ id: 'NewBornEmpty' })}
                            minHeight={480}
                        >
                            <RColumn
                                field="birthdatetime"
                                header={intl.formatMessage({ id: 'NewBornColBirth' })}
                                grow={COLUMN_WIDTH.birth}
                                sortable
                                body={(row: NewBornRowDTO) => (row.birthdatetime ? formatIsoMoment(row.birthdatetime) : EMPTY_VALUE)}
                            />
                            <RColumn
                                field="patientname"
                                header={intl.formatMessage({ id: 'NewBornColMother' })}
                                grow={COLUMN_WIDTH.mother}
                                sortable
                                filter
                                filterElement={stringFilterTemplate}
                                body={(row: NewBornRowDTO) => textCell(row.patientname)}
                            />
                            <RColumn
                                field="age"
                                header={intl.formatMessage({ id: 'NewBornColMotherAge' })}
                                grow={COLUMN_WIDTH.motherAge}
                                className="text-center nv-col-center"
                                sortable
                                filter
                                filterElement={rangeFilter}
                                body={(row: NewBornRowDTO) => numberOrDash(row.age)}
                            />
                            <RColumn
                                field="babyName"
                                header={intl.formatMessage({ id: 'NewBornColBaby' })}
                                grow={COLUMN_WIDTH.baby}
                                sortable
                                filter
                                filterElement={stringFilterTemplate}
                                body={(row: NewBornRowDTO) => textCell(row.babyName)}
                            />
                            <RColumn
                                field="clinicHistoryId"
                                header={intl.formatMessage({ id: 'NewBornColHistory' })}
                                grow={COLUMN_WIDTH.history}
                                sortable
                                filter
                                filterElement={stringFilterTemplate}
                                body={(row: NewBornRowDTO) => textCell(row.clinicHistoryId)}
                            />
                            <RColumn
                                field="sex"
                                header={intl.formatMessage({ id: 'NewBornColSex' })}
                                grow={COLUMN_WIDTH.sex}
                                sortable
                                body={(row: NewBornRowDTO) => textOrDash(row.sex)}
                            />
                            <RColumn
                                field="deliveryType"
                                header={intl.formatMessage({ id: 'NewBornColDelivery' })}
                                grow={COLUMN_WIDTH.delivery}
                                sortable
                                body={(row: NewBornRowDTO) => textCell(row.deliveryType)}
                            />
                            <RColumn
                                field="weight"
                                header={intl.formatMessage({ id: 'NewBornColWeight' })}
                                grow={COLUMN_WIDTH.weight}
                                className="text-center nv-col-center"
                                sortable
                                filter
                                filterElement={rangeFilter}
                                body={(row: NewBornRowDTO) => (row.weight == null
                                    ? EMPTY_VALUE
                                    : `${formatCount(intl, row.weight)} ${intl.formatMessage({ id: 'NewBornUnitGrams' })}`)}
                            />
                            <RColumn
                                field="babyage"
                                header={intl.formatMessage({ id: 'NewBornColWeeks' })}
                                grow={COLUMN_WIDTH.weeks}
                                className="text-center nv-col-center"
                                sortable
                                filter
                                filterElement={rangeFilter}
                                body={(row: NewBornRowDTO) => numberOrDash(row.babyage)}
                            />
                            <RColumn
                                field="apgar"
                                header={intl.formatMessage({ id: 'NewBornColApgar' })}
                                grow={COLUMN_WIDTH.apgar}
                                className="text-center nv-col-center"
                                sortable
                                filter
                                filterElement={rangeFilter}
                                body={(row: NewBornRowDTO) => numberOrDash(row.apgar)}
                            />
                            <RColumn
                                field="apgar5m"
                                header={intl.formatMessage({ id: 'NewBornColApgar5' })}
                                grow={COLUMN_WIDTH.apgar5}
                                className="text-center nv-col-center"
                                sortable
                                filter
                                filterElement={rangeFilter}
                                body={(row: NewBornRowDTO) => numberOrDash(row.apgar5m)}
                            />
                            <RColumn
                                field="placement"
                                header={intl.formatMessage({ id: 'NewBornColPlacement' })}
                                grow={COLUMN_WIDTH.placement}
                                sortable
                                body={(row: NewBornRowDTO) => textCell(row.placement)}
                            />
                            <RColumn
                                field="riskFlags"
                                header={intl.formatMessage({ id: 'NewBornColAlerts' })}
                                grow={COLUMN_WIDTH.alerts}
                                body={(row: NewBornRowDTO) => (row.riskFlags.length === 0 && !row.stillborn ? EMPTY_VALUE : (
                                    <span className="nv-tags">
                                        {row.stillborn && (
                                            <Tag
                                                className="nv-tag-neutral nv-tip"
                                                value={intl.formatMessage({ id: 'NewBornStillbornTag' })}
                                                tabIndex={0}
                                                data-pr-tooltip={intl.formatMessage({ id: 'NewBornNoteStillbornHint' })}
                                            />
                                        )}
                                        {row.riskFlags.map((flag) => (
                                            <Tag
                                                key={flag}
                                                className="nv-tag-warn nv-tip"
                                                icon="pi pi-exclamation-triangle"
                                                value={intl.formatMessage({ id: RISK_LABEL_IDS[flag] })}
                                                tabIndex={0}
                                                data-pr-tooltip={intl.formatMessage({ id: RISK_HINT_IDS[flag] })}
                                            />
                                        ))}
                                    </span>
                                ))}
                            />
                            <RColumn
                                field="needsReview"
                                header={intl.formatMessage({ id: 'NewBornColReview' })}
                                grow={COLUMN_WIDTH.review}
                                body={(row: NewBornRowDTO) => {
                                    if (!row.needsReview) return EMPTY_VALUE;
                                    // El motivo va en el tooltip y en la descripción accesible; la marca lleva texto e ícono
                                    const reasons = row.reviewReasons.map((reason) => intl.formatMessage({ id: REVIEW_REASON_IDS[reason] })).join(' · ');
                                    return (
                                        <Tag
                                            className="nv-tag-neutral nv-tip"
                                            icon="pi pi-exclamation-circle"
                                            value={intl.formatMessage({ id: 'NewBornReviewTag' })}
                                            tabIndex={0}
                                            aria-description={reasons}
                                            data-pr-tooltip={reasons}
                                        />
                                    );
                                }}
                            />
                            <RColumn
                                field="doctorname"
                                header={intl.formatMessage({ id: 'NewBornColDoctor' })}
                                grow={COLUMN_WIDTH.doctor}
                                sortable
                                filter
                                filterElement={stringFilterTemplate}
                                body={(row: NewBornRowDTO) => textCell(row.doctorname)}
                            />
                        </RemoteTable>
                    </div>
                </TabPanel>
                <TabPanel header={intl.formatMessage({ id: 'NewBornTabEvolution' })}>
                    <NewBornEvolution
                        filters={applied}
                        measure={measure}
                        onMeasureChange={setMeasure}
                        granularityChoice={granularityChoice}
                        onGranularityChange={setGranularityChoice}
                        minCases={summary?.minCasesForVerdict ?? FALLBACK_MIN_CASES}
                        onViewPeriod={filtersData.viewRange}
                    />
                </TabPanel>
            </TabView>
        </div>
    );
}
